// src/pages/AddPaymentMethod.tsx
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState, FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { apiGet, apiPost } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

const OPTION_KEYS = {
  "Cash": "balanceSheet.cash",
  "Bank Transfer": "paymentMethods.options.bankTransfer",
  "Visa/MasterCard": "paymentMethods.options.visaMasterCard",
  "Amex": "paymentMethods.options.amex",
  "FPS": "paymentMethods.options.fps",
  "Alipay": "paymentMethods.options.alipay",
  "WeChat Pay": "paymentMethods.options.weChatPay",
  "On Account": "sales.list.onAccount",
  "Other": "paymentMethods.options.other",
} as const;

type Props = {
  onSuccess?: () => void;
};

interface Account {
  id: number;
  name: string;
  type: string;
  kind: string;
  is_system?: boolean;
}

interface PaymentMethod {
  id: number;
  name: string;
  type: string;
  fee_rate: number;
  fixed_fee: number;
  account_id: number;
}

function paymentMethodType(name: string) {
  if (name === "Cash") return "cash";
  if (name === "Bank Transfer") return "bank";
  if (name === "Visa/MasterCard" || name === "Amex") return "card";
  if (name === "Alipay") return "alipay";
  if (name === "WeChat Pay") return "wechat_pay";
  if (name === "On Account") return "on_account";
  return "other";
}

// Root B — ordinary user-selectable payment target: a non-system asset account
// whose normalized kind is cash or bank. Casing/surrounding whitespace are
// normalized for eligibility only (stored data is untouched). Backend may still
// recognize payment/gateway kinds for integrations, but they are not offered as
// ordinary new selections here.
function isOrdinaryPaymentAccount(acc: Account): boolean {
  const norm = (s?: string) => (s ?? "").toLowerCase().trim();
  return (
    acc.is_system !== true &&
    norm(acc.type) === "asset" &&
    (norm(acc.kind) === "cash" || norm(acc.kind) === "bank")
  );
}

export default function AddPaymentMethod({ onSuccess }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const [name, setName] = useState("");
  const [feeRate, setFeeRate] = useState("");
  const [fixedFee, setFixedFee] = useState("");
  const [accountId, setAccountId] = useState<string>("");

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [availableOptions, setAvailableOptions] = useState<string[]>([]);

  const [error, setError] = useState<"nameExists" | "selectReceivingAccount" | "alreadyExists" | "save" | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!company_id) return;

    apiGet<Account[]>(`/accounts?company_id=${company_id}`).then((accs) => {
      const list = Array.isArray(accs) ? accs : [];
      setAccounts(list);

      const eligible = list.filter(isOrdinaryPaymentAccount);
      if (eligible.length === 1) setAccountId(String(eligible[0].id));
    });

    apiGet<PaymentMethod[]>(`/payment-methods?company_id=${company_id}`).then(
      (existing) => {
        const list = Array.isArray(existing) ? existing : [];
        setPaymentMethods(list);

        const usedNames = new Set(list.map((pm) => pm.name.toLowerCase()));
        const all = [
          "Cash",
          "Bank Transfer",
          "Visa/MasterCard",
          "Amex",
          "FPS",
          "Alipay",
          "WeChat Pay",
          "On Account",
          "Other",
        ];
        const available = all.filter((n) => !usedNames.has(n.toLowerCase()));
        setAvailableOptions(available);
        if (available.length > 0) setName(available[0]);
      }
    );
  }, [company_id]);

  const type = useMemo(() => paymentMethodType(name), [name]);

  const receivingAccounts = useMemo(
    () => accounts.filter(isOrdinaryPaymentAccount),
    [accounts]
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    const exists = paymentMethods.some(
      (pm) => pm.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (exists) {
      setError("nameExists");
      return;
    }

    // Validate receiving account selection when shown
    if (receivingAccounts.length > 1 && !accountId) {
      setError("selectReceivingAccount");
      return;
    }

    setSaving(true);
    try {
      await apiPost("/payment-methods", {
        name: trimmedName,
        type,
        // FEE-RATE-SEMANTICS-1 Phase 2B: submit through the explicit percent
        // channel (0..100), never a bare fee_rate. The `feeRate` field already
        // holds a percent (label "Fee Rate (%)"), so NO /100 -- the backend
        // normalizes percent -> canonical fraction.
        fee_rate_percent:
          type === "card" || type === "other" ? Number(feeRate) || 0 : 0,
        fixed_fee: type === "bank" || type === "other" ? Number(fixedFee) || 0 : 0,
        account_id: accountId ? Number(accountId) : null,
        company_id,
      });

      if (onSuccess) onSuccess();
      else navigate(companyRoute(company_id, "/settings/payment-methods"));
    } catch (err: any) {
      if (err?.response?.status === 400) setError("alreadyExists");
      else setError("save");
    } finally {
      setSaving(false);
    }
  };

  // AppleClasses
  const labelCls = `block mb-1 ${A.label}`;

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  const headerShell =
    "sticky top-0 z-30 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 border-b border-gray-100";
  // ✅ locked header spacing tokens: px-2 + mb-2
  const headerInner =
    "w-full max-w-md mx-auto px-2 sm:px-6 pt-4 pb-2 mb-2 flex items-center";

  const readOnlyBox = "rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2";

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      {/* Header */}
      <div className={headerShell}>
        <div className={headerInner}>
          <button
            onClick={() => navigate(-1)}
            className={iconBtn}
            aria-label={t("directory.common.back")}
            type="button"
          >
            <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>

          <h2 className={`flex-1 text-center ${A.title}`}>{t("paymentMethods.addTitle")}</h2>

          <button
            onClick={() => navigate(companyRoute(company_id, "/settings/payment-methods"))}
            className={iconBtn}
            aria-label={t("directory.common.close")}
            type="button"
          >
            <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      {/* Card */}
      <form
        onSubmit={handleSubmit}
        className={`${A.card} w-full max-w-md mx-auto px-2 sm:px-6`}
      >
        <div className="p-5 md:p-6">
          <div className="mb-5">
            <label className={labelCls}>{t("sales.common.paymentMethod")}</label>
            <select
              className={`${A.inputBase} appearance-none`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            >
              {availableOptions.map((option) => (
                <option key={option} value={option}>
                  {t(OPTION_KEYS[option as keyof typeof OPTION_KEYS])}
                </option>
              ))}
            </select>
          </div>

          {(type === "card" || type === "other") && (
            <div className="mb-5">
              <label className={labelCls}>{t("paymentMethods.feeRate")}</label>
              <input
                type="number"
                step="0.01"
                className={A.inputBase}
                value={feeRate}
                onChange={(e) => setFeeRate(e.target.value)}
                min="0"
                placeholder={t("paymentMethods.percentExample")}
                required={type === "card"}
                inputMode="decimal"
              />
            </div>
          )}

          {(type === "bank" || type === "other") && (
            <div className="mb-5">
              <label className={labelCls}>{t("paymentMethods.fixedFeeCurrency")}</label>
              <input
                type="number"
                step="0.01"
                className={A.inputBase}
                value={fixedFee}
                onChange={(e) => setFixedFee(e.target.value)}
                min="0"
                placeholder={t("paymentMethods.fixedAmountExample")}
                required={type === "bank"}
                inputMode="decimal"
              />
            </div>
          )}

          {receivingAccounts.length > 1 && (
            <div className="mb-6">
              <label className={labelCls}>{t("paymentMethods.receivingAccount")}</label>
              <select
                className={`${A.inputBase} appearance-none`}
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                required
              >
                <option value="">{t("paymentMethods.selectReceivingAccount")}</option>
                {receivingAccounts.map((acc) => (
                  <option key={acc.id} value={String(acc.id)}>
                    {acc.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {receivingAccounts.length === 1 && (
            <div className="mb-6">
              <label className={labelCls}>{t("paymentMethods.receivingAccount")}</label>
              <div className={`${readOnlyBox} ${A.body}`}>{receivingAccounts[0].name}</div>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
              {t(error === "nameExists" ? "paymentMethods.errors.nameExists" : error === "selectReceivingAccount" ? "paymentMethods.errors.selectReceivingAccount" : error === "alreadyExists" ? "paymentMethods.errors.alreadyExists" : "paymentMethods.errors.save")}
            </div>
          )}

          <Button
            type="submit"
            disabled={saving}
            variant="primary"
            minWidth="100%"
            className="w-full justify-center"
          >
            {saving ? t("directory.common.savingDots") : t("paymentMethods.saveMethod")}
          </Button>
        </div>
      </form>
    </div>
  );
}
